/**
 * BOSS avans kararı modal'ı.
 * Onayla / Reddet / Karşı Teklif
 */

import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { CheckCircle2, XCircle, RefreshCw } from 'lucide-react'
import {
  avansOnayla,
  avansReddet,
  avansKarsiTeklif,
  type Avans,
} from '@/lib/services/avans-service'
import { useAuth } from '@/contexts/AuthContext'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  avans: Avans | null
  personel: KullaniciPersonel | null
  onKapat: () => void
  onKaydedildi: () => void
}

type Karar = null | 'onay' | 'red' | 'karsiTeklif'

export function AvansKararModal({ acik, avans, personel, onKapat, onKaydedildi }: Props) {
  const { firebaseUser } = useAuth()
  const [karar, setKarar] = useState<Karar>(null)
  const [redSebebi, setRedSebebi] = useState('')
  const [karsiTeklifTutar, setKarsiTeklifTutar] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [kaydediliyor, setKaydediliyor] = useState(false)

  useEffect(() => {
    if (!acik) return
    setKarar(null)
    setRedSebebi('')
    setKarsiTeklifTutar('')
    setHata(null)
  }, [acik])

  if (!avans || !personel) return null

  async function onayla() {
    if (!firebaseUser) return
    setKaydediliyor(true)
    setHata(null)
    try {
      await avansOnayla(avans!.id, firebaseUser.uid)
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  async function reddet() {
    if (!firebaseUser) return
    setKaydediliyor(true)
    setHata(null)
    try {
      await avansReddet(avans!.id, firebaseUser.uid, redSebebi)
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  async function karsiTeklif() {
    if (!firebaseUser) return
    const sayi = parseFloat(karsiTeklifTutar)
    if (isNaN(sayi) || sayi <= 0) {
      setHata('Geçerli bir tutar gir.')
      return
    }
    if (sayi >= avans!.talepTutari) {
      setHata('Karşı teklif, talep tutarından düşük olmalı (yoksa direkt onayla).')
      return
    }
    setKaydediliyor(true)
    setHata(null)
    try {
      await avansKarsiTeklif(avans!.id, sayi, firebaseUser.uid)
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  const tarihEtiketi = avans.talepTarihi.toDate().toLocaleString('tr-TR', {
    day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  })

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Avans Talebi" boyut="md">
      <div className="space-y-4">
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-3">
          <p className="font-semibold text-brand-900">{personel.isim}</p>
          <p className="text-xs text-brand-700 mt-1">{tarihEtiketi}</p>
          <p className="text-3xl font-bold text-brand-900 mt-2">
            {avans.talepTutari.toLocaleString('de-DE')} ₺
          </p>
          {avans.aciklama && (
            <p className="text-sm text-brand-800 mt-2 italic">"{avans.aciklama}"</p>
          )}
        </div>

        {!karar && (
          <>
            <p className="text-sm text-gray-700 font-medium">Karar:</p>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => setKarar('onay')}
                className="p-3 rounded-xl border-2 border-green-200 bg-green-50 text-green-700 hover:border-green-400 flex items-center gap-3 transition-colors"
              >
                <CheckCircle2 className="w-6 h-6" />
                <div className="flex-1 text-left">
                  <p className="font-bold text-sm">Onayla</p>
                  <p className="text-xs">Tam {avans.talepTutari.toLocaleString('de-DE')} ₺ onaylanır</p>
                </div>
              </button>

              <button
                onClick={() => setKarar('karsiTeklif')}
                className="p-3 rounded-xl border-2 border-purple-200 bg-purple-50 text-purple-700 hover:border-purple-400 flex items-center gap-3 transition-colors"
              >
                <RefreshCw className="w-6 h-6" />
                <div className="flex-1 text-left">
                  <p className="font-bold text-sm">Karşı Teklif</p>
                  <p className="text-xs">Daha düşük bir tutar öner — personel kabul/red eder</p>
                </div>
              </button>

              <button
                onClick={() => setKarar('red')}
                className="p-3 rounded-xl border-2 border-red-200 bg-red-50 text-red-700 hover:border-red-400 flex items-center gap-3 transition-colors"
              >
                <XCircle className="w-6 h-6" />
                <div className="flex-1 text-left">
                  <p className="font-bold text-sm">Reddet</p>
                  <p className="text-xs">Talebi reddet (sebep yazabilirsin)</p>
                </div>
              </button>
            </div>
          </>
        )}

        {karar === 'onay' && (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
              <p className="font-bold text-green-900">
                {avans.talepTutari.toLocaleString('de-DE')} ₺ onaylanacak
              </p>
              <p className="text-xs text-green-700 mt-1">
                Onaylandıktan sonra Avanslar sayfasında "Ödendi" olarak işaretleyince
                ay sonu maaşından düşer.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setKarar(null)} className="btn-secondary flex-1" disabled={kaydediliyor}>
                Geri
              </button>
              <button
                onClick={onayla}
                disabled={kaydediliyor}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl"
              >
                {kaydediliyor ? 'Onaylanıyor...' : 'Onayla'}
              </button>
            </div>
          </div>
        )}

        {karar === 'karsiTeklif' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Karşı Teklif Tutarı (₺) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max={avans.talepTutari - 1}
                step="any"
                value={karsiTeklifTutar}
                onChange={(e) => setKarsiTeklifTutar(e.target.value)}
                className="input-base text-lg font-semibold"
                placeholder="0"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                Talep edilen {avans.talepTutari.toLocaleString('de-DE')} ₺'den düşük olmalı.
              </p>
            </div>
            <p className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-2">
              💡 Personele bildirim gidecek. Kabul ederse onaylanmış sayılır,
              reddederse talep iptal olur.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setKarar(null)} className="btn-secondary flex-1" disabled={kaydediliyor}>
                Geri
              </button>
              <button
                onClick={karsiTeklif}
                disabled={kaydediliyor}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 rounded-xl"
              >
                {kaydediliyor ? 'Gönderiliyor...' : 'Karşı Teklif Gönder'}
              </button>
            </div>
          </div>
        )}

        {karar === 'red' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Red Sebebi (opsiyonel)
              </label>
              <textarea
                rows={3}
                value={redSebebi}
                onChange={(e) => setRedSebebi(e.target.value)}
                className="input-base"
                placeholder="Personele görünecek..."
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setKarar(null)} className="btn-secondary flex-1" disabled={kaydediliyor}>
                Geri
              </button>
              <button
                onClick={reddet}
                disabled={kaydediliyor}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl"
              >
                {kaydediliyor ? 'Reddediliyor...' : 'Reddet'}
              </button>
            </div>
          </div>
        )}

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}
      </div>
    </Modal>
  )
}
